import { useCallback, useRef, useState } from 'react';
import { EditorState } from 'draft-js';
import ID from 'react-form-builder2/lib/UUID';

import { MultiSelectOptionItem, MultiSelectOptionsEditorProps } from './types';
import { editorStateToHtml, htmlToEditorState, stripHtml } from './utils';

interface EditorMap {
  [key: string]: EditorState;
}

interface CollapsedMap {
  [key: string]: boolean;
}

const buildInitialState = (element: any) => {
  const options: MultiSelectOptionItem[] = element?.options || [];
  const editors: EditorMap = {};
  const collapsed: CollapsedMap = {};

  options.forEach((option, index) => {
    if (!option.key) option.key = ID.uuid();
    editors[option.key] = htmlToEditorState(option.html || option.text || '');
    collapsed[option.key] = index > 0;
  });

  return { editors, collapsed };
};

export const useMultiSelectOptions = ({
  element,
  preview,
  updateElement,
}: MultiSelectOptionsEditorProps) => {
  const initial = useRef(buildInitialState(element)).current;

  const [editors, setEditors] = useState<EditorMap>(initial.editors);
  const [collapsed, setCollapsed] = useState<CollapsedMap>(initial.collapsed);
  const [version, setVersion] = useState(0);

  const getOptions = useCallback((): MultiSelectOptionItem[] => {
    if (!Array.isArray(element.options)) element.options = [];
    return element.options;
  }, [element]);

  const commit = useCallback(() => {
    setVersion((current) => current + 1);
    updateElement?.call(preview, element);
  }, [element, preview, updateElement]);

  const toggleRow = useCallback((key: string) => {
    setCollapsed((current) => ({ ...current, [key]: !current[key] }));
  }, []);

  const changeLabel = useCallback(
    (index: number, editorState: EditorState) => {
      const option = getOptions()[index];
      if (!option) return;

      const html = editorStateToHtml(editorState);

      option.html = html;
      option.text = stripHtml(html);
      if (!option.value) option.value = String(index + 1);

      setEditors((current) => ({ ...current, [option.key]: editorState }));
      commit();
    },
    [commit, getOptions],
  );

  const changeValue = useCallback(
    (index: number, value: string) => {
      const option = getOptions()[index];
      if (!option) return;

      option.value = value === '' ? String(index + 1) : value;
      commit();
    },
    [commit, getOptions],
  );

  const changeImage = useCallback(
    (index: number, imageUrl: string) => {
      const option = getOptions()[index];
      if (!option) return;

      if (imageUrl.trim()) {
        option.imageUrl = imageUrl;
      } else {
        delete option.imageUrl;
      }
      commit();
    },
    [commit, getOptions],
  );

  const changeIcon = useCallback(
    (index: number, icon: string) => {
      const option = getOptions()[index];
      if (!option) return;

      if (icon.trim()) {
        option.icon = icon;
      } else {
        delete option.icon;
      }
      commit();
    },
    [commit, getOptions],
  );

  const changeExclusive = useCallback(
    (index: number, exclusive: boolean) => {
      const option = getOptions()[index];
      if (!option) return;

      if (exclusive) {
        option.exclusive = true;
      } else {
        delete option.exclusive;
      }
      commit();
    },
    [commit, getOptions],
  );

  const addOption = useCallback(() => {
    const options = getOptions();
    const key = ID.uuid();
    const position = options.length + 1;
    const text = `Option ${position}`;

    options.push({
      value: String(position),
      text,
      html: `<p>${text}</p>`,
      key,
    });

    setEditors((current) => ({ ...current, [key]: htmlToEditorState(text) }));
    commit();
  }, [commit, getOptions]);

  const removeOption = useCallback(
    (index: number) => {
      const [removed] = getOptions().splice(index, 1);

      if (removed) {
        setEditors((current) => {
          const next = { ...current };
          delete next[removed.key];
          return next;
        });
      }

      commit();
    },
    [commit, getOptions],
  );

  const moveOption = useCallback(
    (index: number, delta: number) => {
      const options = getOptions();
      const target = index + delta;
      if (target < 0 || target >= options.length) return;

      [options[index], options[target]] = [options[target], options[index]];
      commit();
    },
    [commit, getOptions],
  );

  return {
    options: getOptions(),
    editors,
    collapsed,
    version,
    toggleRow,
    changeLabel,
    changeValue,
    changeImage,
    changeIcon,
    changeExclusive,
    addOption,
    removeOption,
    moveOption,
  };
};
